"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AuraPresence } from "../aura-presence";
import { Button } from "@/components/ui/button";
import type {
  AgentStatus,
  AgentType,
  AgentProfile,
  ConversationMessage,
  CommandContext,
  SuggestedCommand,
  MessageSourceType,
} from "@/lib/aura/types";
import {
  Send,
  Paperclip,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Shield,
  Clock,
  Database,
  Zap,
  Brain,
  Users,
  Eye,
  HandIcon,
} from "lucide-react";

// Extended agent selector type includes "all"
type AgentSelectorId = AgentType | "all";
const COMMAND_THREAD_PAGE_SIZE = 12;

interface AgentSelectorData {
  id: AgentSelectorId;
  name: string;
  role: string;
  status: "online" | "busy" | "offline" | "error" | "available";
  description: string;
}

function mapAgentStatusToSelectorStatus(
  status: AgentStatus["status"]
): AgentSelectorData["status"] {
  return status;
}

function buildAgentSelectorData(agents: AgentStatus[]): AgentSelectorData[] {
  const mappedAgents: AgentSelectorData[] = agents.map((agent) => ({
    id: agent.id,
    name: agent.name,
    role: agent.role,
    status: mapAgentStatusToSelectorStatus(agent.status),
    description: agent.currentTask || agent.role,
  }));

  return [
    ...mappedAgents,
    {
      id: "all" as const,
      name: "All Agents",
      role: "Multi-Agent Thread",
      status: "available" as const,
      description: "Coordinated multi-agent decision thread",
    },
  ];
}

// Agent Selector Item
function AgentSelectorItem({
  agent,
  isSelected,
  onSelect,
}: {
  agent: AgentSelectorData;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const statusColors: Record<AgentSelectorData["status"], string> = {
    online: "bg-success",
    busy: "bg-warning",
    offline: "bg-muted-foreground",
    error: "bg-destructive",
    available: "bg-muted-foreground/50",
  };

  const statusLabels: Record<AgentSelectorData["status"], string> = {
    online: "Online",
    busy: "Busy",
    offline: "Offline",
    error: "Error",
    available: "Available",
  };

  return (
    <button
      aria-label={`Select ${agent.name}`}
      onClick={onSelect}
      className={cn(
        "min-w-[13rem] text-left p-3 rounded-lg border transition-all lg:w-full lg:min-w-0",
        isSelected
          ? "bg-accent/10 border-accent/40"
          : "bg-transparent border-transparent hover:bg-muted/30 hover:border-border/50"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-sm font-medium",
                isSelected ? "text-accent" : "text-foreground"
              )}
            >
              {agent.name}
            </span>
            {isSelected && <ChevronRight className="w-3 h-3 text-accent" />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {agent.role}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={cn(
              "w-1.5 h-1.5 rounded-full",
              statusColors[agent.status]
            )}
          />
          <span className="text-[10px] text-muted-foreground">
            {statusLabels[agent.status]}
          </span>
        </div>
      </div>
    </button>
  );
}

// Message Bubble
function MessageBubble({ message }: { message: ConversationMessage }) {
  const sourceConfig: Record<
    MessageSourceType,
    {
      label: string;
      borderColor: string;
      icon: React.ReactNode;
      bgColor: string;
    }
  > = {
    human: {
      label: "Founder",
      borderColor: "border-r-muted-foreground",
      icon: null,
      bgColor: "bg-muted/20",
    },
    aura: {
      label: "AURA",
      borderColor: "border-l-accent",
      icon: <AuraPresence size="sm" intensity="normal" className="shrink-0" />,
      bgColor: "bg-accent/5",
    },
    "founder-agent": {
      label: "Founder Agent",
      borderColor: "border-l-success",
      icon: (
        <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center shrink-0">
          <Zap className="w-3 h-3 text-success" />
        </div>
      ),
      bgColor: "bg-success/5",
    },
    codex: {
      label: "Codex",
      borderColor: "border-l-warning",
      icon: (
        <div className="w-6 h-6 rounded-full bg-warning/20 flex items-center justify-center shrink-0">
          <Database className="w-3 h-3 text-warning" />
        </div>
      ),
      bgColor: "bg-warning/5",
    },
    claude: {
      label: "Claude",
      borderColor: "border-l-[#D4A574]",
      icon: (
        <div className="w-6 h-6 rounded-full bg-[#D4A574]/20 flex items-center justify-center shrink-0">
          <Brain className="w-3 h-3 text-[#D4A574]" />
        </div>
      ),
      bgColor: "bg-[#D4A574]/5",
    },
    core: {
      label: "Core",
      borderColor: "border-l-muted-foreground",
      icon: (
        <div className="w-6 h-6 rounded-full bg-muted/30 flex items-center justify-center shrink-0">
          <Shield className="w-3 h-3 text-muted-foreground" />
        </div>
      ),
      bgColor: "bg-muted/10",
    },
    system: {
      label: "System",
      borderColor: "border-l-muted-foreground/50",
      icon: (
        <div className="w-6 h-6 rounded-full bg-muted/20 flex items-center justify-center shrink-0">
          <Shield className="w-3 h-3 text-muted-foreground" />
        </div>
      ),
      bgColor: "bg-muted/5",
    },
    evidence: {
      label: "Evidence",
      borderColor: "border-l-accent/50",
      icon: (
        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
          <FileText className="w-3 h-3 text-accent" />
        </div>
      ),
      bgColor: "bg-accent/5",
    },
    approval: {
      label: "Approval Request",
      borderColor: "border-l-warning",
      icon: (
        <div className="w-6 h-6 rounded-full bg-warning/20 flex items-center justify-center shrink-0">
          <Clock className="w-3 h-3 text-warning" />
        </div>
      ),
      bgColor: "bg-warning/5",
    },
    memory: {
      label: "Memory Note",
      borderColor: "border-l-muted-foreground/50",
      icon: (
        <div className="w-6 h-6 rounded-full bg-muted/20 flex items-center justify-center shrink-0">
          <Database className="w-3 h-3 text-muted-foreground" />
        </div>
      ),
      bgColor: "bg-muted/5",
    },
    "risk-warning": {
      label: "Risk Warning",
      borderColor: "border-l-destructive",
      icon: (
        <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-3 h-3 text-destructive" />
        </div>
      ),
      bgColor: "bg-destructive/5",
    },
  };

  const config = sourceConfig[message.source];
  const isHuman = message.source === "human";
  const targetLabel = message.metadata?.targetAgent
    ? message.metadata.targetAgent === "all"
      ? "All Agents"
      : formatAgentName(message.metadata.targetAgent)
    : undefined;
  const channelDetail = message.metadata?.responderRuntime
    ? message.metadata.channelProxy
      ? `proxy via ${message.metadata.responderRuntime}`
      : message.metadata.responderRuntime
    : undefined;

  const formatRelativeTimestamp = (ts: string) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };
  const formatAbsoluteTimestamp = (ts: string) => {
    const date = new Date(ts);

    if (Number.isNaN(date.getTime())) return ts;

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };
  const absoluteTimestamp = formatAbsoluteTimestamp(message.timestamp);

  if (isHuman) {
    return (
      <div className="flex justify-end">
        <div className="max-w-lg">
          <div
            className={cn(
              "rounded-lg rounded-tr-sm p-4 border-r-2",
              config.borderColor,
              config.bgColor,
              "border border-border/30"
            )}
          >
            <p className="text-sm text-foreground leading-relaxed">
              {message.content}
            </p>
          </div>
          <div className="flex justify-end mt-1.5">
            <span className="text-[10px] text-muted-foreground">
              {config.label}
              {targetLabel ? ` to ${targetLabel}` : ""} ·{" "}
              <time dateTime={message.timestamp} title={absoluteTimestamp}>
                {absoluteTimestamp}
              </time>
              {" · "}
              {formatRelativeTimestamp(message.timestamp)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      {config.icon}
        <div className="flex-1 max-w-2xl min-w-0">
        <div
          className={cn(
            "rounded-lg rounded-tl-sm p-4 border-l-2",
            config.borderColor,
            config.bgColor,
            "border border-border/30"
          )}
        >
            <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
            {message.content}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[10px] text-muted-foreground">
            {message.agentName || config.label} ·{" "}
            <time dateTime={message.timestamp} title={absoluteTimestamp}>
              {absoluteTimestamp}
            </time>
            {" · "}
            {formatRelativeTimestamp(message.timestamp)}
          </span>
          {channelDetail && (
            <span className="rounded-full border border-border/50 px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {channelDetail}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Suggested Command Button
function SuggestedCommandButton({
  command,
  onClick,
}: {
  command: SuggestedCommand;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 text-xs px-3 py-1.5 rounded-md border border-border bg-muted/20 text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:border-accent/30 transition-all"
    >
      {command.text}
    </button>
  );
}

// Target Selector for Composer
function TargetSelector({
  selectedTarget,
  onSelect,
  agents,
}: {
  selectedTarget: AgentSelectorId;
  onSelect: (target: AgentSelectorId) => void;
  agents: AgentSelectorData[];
}) {
  return (
    <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-md border border-border/50 bg-muted/20 px-2 py-1">
      <span className="mr-1 shrink-0 text-[10px] text-muted-foreground">Send to:</span>
      {agents.map((agent) => (
        <button
          key={agent.id}
          aria-label={`Send to ${agent.name}`}
          onClick={() => onSelect(agent.id)}
          className={cn(
            "shrink-0 text-[10px] px-2 py-0.5 rounded transition-all",
            selectedTarget === agent.id
              ? "bg-accent/20 text-accent"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {agent.name}
        </button>
      ))}
    </div>
  );
}

function formatAgentName(agentId: AgentSelectorId): string {
  const labels: Record<AgentSelectorId, string> = {
    aura: "AURA",
    "founder-agent": "Founder Agent",
    codex: "Codex",
    claude: "Claude",
    core: "Core",
    all: "All Agents",
  };
  return labels[agentId];
}

interface CommandAuraProps {
  agents: AgentStatus[];
  thread: ConversationMessage[];
  context: CommandContext;
  suggestedCommands: SuggestedCommand[];
  selectedAgentId: AgentType | "all";
  agentContext?: AgentProfile | null;
  onSelectAgent?: (agentId: AgentType | "all") => void;
  onSendMessage?: (message: string, targetAgent: AgentType | "all") => void;
  onAttachEvidence?: () => void;
  onAskFounderAgent?: () => void;
  onRequestClaudeReview?: () => void;
  onSendToCodex?: () => void;
  onRecordThroughCore?: () => void;
  onHoldProduction?: () => void;
  onAcceptRecommendation?: () => void;
  onSuggestedCommand?: (command: SuggestedCommand) => void;
}

export function CommandAura({
  agents,
  thread,
  context,
  suggestedCommands,
  selectedAgentId,
  agentContext,
  onSelectAgent,
  onSendMessage,
  onAttachEvidence,
  onAskFounderAgent,
  onRequestClaudeReview,
  onSendToCodex,
  onRecordThroughCore,
  onHoldProduction,
  onAcceptRecommendation,
  onSuggestedCommand,
}: CommandAuraProps) {
  const [sendTarget, setSendTarget] = useState<AgentSelectorId>(selectedAgentId);
  const [inputValue, setInputValue] = useState("");
  const [visibleMessageCount, setVisibleMessageCount] = useState(
    COMMAND_THREAD_PAGE_SIZE
  );
  const streamRef = useRef<HTMLDivElement | null>(null);
  const suppressNextAutoScrollRef = useRef(false);

  const agentSelectorData = buildAgentSelectorData(agents);
  const selectedAgentData = agentSelectorData.find(
    (a) => a.id === selectedAgentId
  );
  const displayedThread = useMemo(
    () =>
      selectedAgentId === "all"
        ? thread
        : thread.filter(
            (message) =>
              message.source === selectedAgentId ||
              message.metadata?.targetAgent === selectedAgentId ||
              message.source === "system" ||
              message.source === "risk-warning"
          ),
    [selectedAgentId, thread]
  );
  const visibleThread = useMemo(
    () => displayedThread.slice(-visibleMessageCount),
    [displayedThread, visibleMessageCount]
  );
  const hiddenOlderMessageCount = Math.max(
    displayedThread.length - visibleThread.length,
    0
  );

  useEffect(() => {
    setSendTarget(selectedAgentId);
    setVisibleMessageCount(COMMAND_THREAD_PAGE_SIZE);
  }, [selectedAgentId]);

  useEffect(() => {
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }

    const scrollToLatest = () => {
      const stream = streamRef.current;
      if (!stream) return;
      stream.scrollTop = stream.scrollHeight;
    };

    window.requestAnimationFrame(scrollToLatest);
    window.setTimeout(scrollToLatest, 100);
    window.setTimeout(scrollToLatest, 350);
    window.setTimeout(scrollToLatest, 900);
  }, [selectedAgentId, visibleThread.length, displayedThread.length]);

  const showHoldProduction = context.recommendedAction.type === "hold";

  const getHeaderTitle = () => {
    if (selectedAgentId === "all") return "Multi-Agent Operational Thread";
    return `Command ${selectedAgentData?.name}`;
  };

  const handleSend = () => {
    if (inputValue.trim() && onSendMessage) {
      onSendMessage(inputValue.trim(), sendTarget);
      setInputValue("");
    }
  };
  const handleSelectAgent = (agentId: AgentSelectorId) => {
    setSendTarget(agentId);
    onSelectAgent?.(agentId);
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden pb-16 lg:flex-row lg:pb-0">
      {/* Left Panel: Agent Selector */}
      <div className="border-b border-border bg-panel/30 lg:flex lg:w-64 lg:shrink-0 lg:flex-col lg:border-b-0 lg:border-r">
        <div className="p-4 border-b border-border">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Conversation Target
          </h2>
        </div>
        <div className="flex gap-2 overflow-x-auto p-3 lg:flex-1 lg:flex-col lg:gap-0 lg:space-y-1 lg:overflow-y-auto lg:overflow-x-hidden">
          {agentSelectorData.map((agent) => (
            <AgentSelectorItem
              key={agent.id}
              agent={agent}
              isSelected={selectedAgentId === agent.id}
              onSelect={() => handleSelectAgent(agent.id)}
            />
          ))}
        </div>
      </div>

      {/* Center Panel: Dynamic Conversation Thread */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Thread Header */}
        <div className="border-b border-border p-4 flex items-center gap-3 bg-panel/20">
          {selectedAgentId === "aura" && (
            <AuraPresence size="md" intensity="strong" />
          )}
          {selectedAgentId === "founder-agent" && (
            <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-success" />
            </div>
          )}
          {selectedAgentId === "codex" && (
            <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center">
              <Database className="w-4 h-4 text-warning" />
            </div>
          )}
          {selectedAgentId === "claude" && (
            <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center">
              <Brain className="w-4 h-4 text-[#D4A574]" />
            </div>
          )}
          {selectedAgentId === "core" && (
            <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
              <Shield className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          {selectedAgentId === "all" && (
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              <Users className="w-4 h-4 text-accent" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-foreground">
              {getHeaderTitle()}
            </h1>
            <p className="truncate text-xs text-muted-foreground">
              {selectedAgentData?.description}
            </p>
          </div>
        </div>

        {agentContext && (
          <div className="border-b border-border/60 bg-accent/5 px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-accent">
                  Agent Studio Context
                </p>
                <p className="mt-0.5 truncate text-sm text-foreground">
                  {agentContext.identity.displayName} · {agentContext.capability.role} ·{" "}
                  {agentContext.identity.domain.replace("-", " ")}
                </p>
              </div>
              <span className="shrink-0 rounded-full border border-border/60 px-2 py-1 text-[10px] text-muted-foreground">
                Routed through {formatAgentName(selectedAgentId)}
              </span>
            </div>
          </div>
        )}

        {/* Message Stream - add bottom padding for sticky composer */}
        <div
          ref={streamRef}
          className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 pb-6 sm:p-5 lg:p-6"
          data-uat="command-aura-thread"
        >
          {hiddenOlderMessageCount > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                className="rounded-full border border-border/60 bg-panel/70 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-accent/40 hover:text-foreground"
                onClick={() => {
                  suppressNextAutoScrollRef.current = true;
                  setVisibleMessageCount((current) =>
                    Math.min(current + COMMAND_THREAD_PAGE_SIZE, displayedThread.length)
                  );
                }}
              >
                Load {Math.min(COMMAND_THREAD_PAGE_SIZE, hiddenOlderMessageCount)} older
                messages · {hiddenOlderMessageCount} hidden
              </button>
            </div>
          )}
          {visibleThread.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {displayedThread.length === 0 && (
            <div className="mx-auto max-w-xl rounded-lg border border-border/50 bg-muted/10 p-5 text-center">
              <p className="text-sm text-foreground">
                No messages for {formatAgentName(selectedAgentId)} yet.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Send a command to start this agent-specific thread.
              </p>
            </div>
          )}
        </div>

        {/* Sticky Governed Command Composer */}
        <div className="shrink-0 border-t border-border bg-panel/95 backdrop-blur-sm">
          {/* Suggested Commands - compact row */}
          <div className="px-4 py-2.5 border-b border-border/50">
            <div className="flex items-start gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide shrink-0">
                Suggested:
              </span>
              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:flex-wrap lg:overflow-visible lg:pb-0">
                {suggestedCommands.slice(0, 4).map((cmd) => (
                  <SuggestedCommandButton
                    key={cmd.id}
                    command={cmd}
                    onClick={() => onSuggestedCommand?.(cmd)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Main Composer Area */}
          <div className="p-4">
            {/* Input Row */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 flex min-w-0 items-center gap-2 bg-muted/20 rounded-lg border border-border px-4 py-2.5">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Ask AURA, Founder Agent, Codex, or Claude what should happen next..."
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <Button
                size="icon"
                className="bg-accent/20 text-accent hover:bg-accent/30 border border-accent/30 shrink-0"
                onClick={handleSend}
                aria-label={`Send command to ${formatAgentName(sendTarget)}`}
                title={`Send command to ${formatAgentName(sendTarget)}`}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>

            {/* Bottom Controls Row */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              {/* Target Selector */}
              <TargetSelector
                selectedTarget={sendTarget}
                onSelect={handleSelectAgent}
                agents={agentSelectorData}
              />

              {/* Secondary Actions - Compact Chips */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:overflow-visible lg:pb-0">
                <button
                  onClick={onAttachEvidence}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
                >
                  <Paperclip className="w-3 h-3" />
                  <span>Evidence</span>
                </button>
                <button
                  onClick={onRequestClaudeReview}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
                >
                  <Eye className="w-3 h-3" />
                  <span>Review</span>
                </button>
                <button
                  onClick={onRecordThroughCore}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
                >
                  <Shield className="w-3 h-3" />
                  <span>Core</span>
                </button>
                {showHoldProduction && (
                  <button
                    onClick={onHoldProduction}
                    className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-warning/40 text-warning hover:bg-warning/10 transition-all"
                  >
                    <HandIcon className="w-3 h-3" />
                    <span>Hold</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel: Context / Evidence Panel */}
      <div className="hidden border-t border-border bg-panel/30 lg:flex lg:w-80 lg:shrink-0 lg:flex-col lg:border-l lg:border-t-0">
        <div className="p-4 border-b border-border">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Operational Context
          </h2>
        </div>
        <div className="space-y-6 p-4 lg:flex-1 lg:overflow-y-auto">
          {/* Current State */}
          <div>
            <h3 className="text-xs font-medium text-foreground mb-3">
              Current State
            </h3>
            <div className="glass-panel rounded-lg p-3">
              <p className="text-xs text-foreground">{context.currentState}</p>
            </div>
          </div>

          {/* Related Decision Topic */}
          {context.relatedDecisionTopic && (
            <div>
              <h3 className="text-xs font-medium text-foreground mb-3">
                Related Decision Topic
              </h3>
              <div className="glass-panel rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full bg-accent/20 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  </div>
                  <span className="text-xs font-medium text-foreground">
                    {context.relatedDecisionTopic}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Current Blocker */}
          {context.currentBlocker && (
            <div>
              <h3 className="text-xs font-medium text-foreground mb-3">
                Current Blocker
              </h3>
              <div className="glass-panel rounded-lg p-3 border-l-2 border-l-warning">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3 h-3 text-warning" />
                  <span className="text-xs font-medium text-warning">
                    {context.currentBlocker}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Recommended Next Action */}
          <div>
            <h3 className="text-xs font-medium text-foreground mb-3">
              Recommended Next Action
            </h3>
            <div className="glass-panel rounded-lg p-3 border-l-2 border-l-accent">
              <p className="text-xs text-foreground mb-2">
                {context.recommendedAction.text}
              </p>
              <Button
                size="sm"
                className="w-full text-xs bg-accent/20 text-accent hover:bg-accent/30 border border-accent/30"
                onClick={onAcceptRecommendation}
              >
                <CheckCircle2 className="w-3 h-3 mr-1.5" />
                Accept Recommendation
              </Button>
            </div>
          </div>

          {/* Memory Notes */}
          {context.memoryNotes.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-foreground mb-3">
                Memory Notes
              </h3>
              <div className="space-y-2">
                {context.memoryNotes.map((note, index) => (
                  <div key={index} className="glass-panel rounded-lg p-3">
                    <p className="text-[11px] text-muted-foreground">{note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
